// frontend/src/services/encryptionService.js
// Zero-knowledge encryption service - only user can decrypt their data

import nacl from 'tweetnacl';
import { encodeUTF8, decodeUTF8, encodeBase64, decodeBase64 } from 'tweetnacl-util';

class EncryptionService {
  constructor() {
    this.userKey = null;
    this.keyCache = new Map(); // Cache derived keys per user session
  }

  /**
   * Simple hash function fallback for browsers without Web Crypto API
   */
  async simpleHash(str) {
    const encoder = new TextEncoder();
    const data = encoder.encode(str);
    
    if (window.crypto && window.crypto.subtle) {
      const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
      return new Uint8Array(hashBuffer);
    }
    
    // Last resort: very basic hash (not cryptographically secure)
    // Using fallback hash implementation
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    // Expand to 32 bytes
    const result = new Uint8Array(32);
    for (let i = 0; i < 32; i++) {
      result[i] = (hash >> (i % 4 * 8)) & 0xFF;
    }
    return result;
  }

  /**
   * Derive encryption key from user password + user ID
   * This ensures unique keys per user, derived deterministically
   */
  async deriveUserKey(password, userId) {
    
    // Check if Web Crypto API is available
    if (!window.crypto || !window.crypto.subtle) {
      // Web Crypto API not available, using fallback
      // Simple fallback using built-in crypto (less secure but functional)
      const combined = password + userId + 'ideahive-salt';
      const hash = await this.simpleHash(combined);
      return new Uint8Array(hash.slice(0, 32));
    }
    
    // Use Web Crypto API for key derivation (PBKDF2)
    const encoder = new TextEncoder();
    const keyMaterial = await window.crypto.subtle.importKey(
      'raw',
      encoder.encode(password + userId), // Salt with user ID
      'PBKDF2',
      false,
      ['deriveKey']
    );

    const derivedKey = await window.crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: encoder.encode(userId), // User ID as salt
        iterations: 100000, // Strong iteration count
        hash: 'SHA-256'
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      true,
      ['encrypt', 'decrypt']
    );

    // Export for use with tweetnacl
    const keyBuffer = await window.crypto.subtle.exportKey('raw', derivedKey);
    this.userKey = new Uint8Array(keyBuffer).slice(0, 32); // 32 bytes for tweetnacl
    this.keyCache.set(userId, this.userKey);
    
    return this.userKey;
  }

  /**
   * Initialize encryption for user session
   * Call this after successful login
   */
  async initializeForUser(user, password = null) {
    const userId = user.uid;
    
    // Check cache first
    if (this.keyCache.has(userId)) {
      this.userKey = this.keyCache.get(userId);
      return this.userKey;
    }

    // For password-based auth, derive from password
    if (password) {
      return await this.deriveUserKey(password, userId);
    }

    // For social auth (Google), derive from user data
    // Use email + uid as deterministic source
    const userSeed = user.email + user.uid;
    return await this.deriveUserKey(userSeed, userId);
  }

  /**
   * Encrypt text content before sending to backend
   */
  encryptText(plaintext) {
    if (!this.userKey) {
      throw new Error('Encryption not initialized. Call initializeForUser first.');
    }

    if (!plaintext || typeof plaintext !== 'string') {
      return plaintext; // Don't encrypt empty/invalid data
    }

    try {
      const nonce = nacl.randomBytes(24); // 24 bytes for XSalsa20Poly1305
      const messageUint8 = encodeUTF8(plaintext);
      const encrypted = nacl.secretbox(messageUint8, nonce, this.userKey);

      // Combine nonce + encrypted data for storage
      const combined = new Uint8Array(nonce.length + encrypted.length);
      combined.set(nonce);
      combined.set(encrypted, nonce.length);

      return encodeBase64(combined);
    } catch (error) {
      throw new Error('Failed to encrypt data');
    }
  }

  /**
   * Decrypt text content after receiving from backend
   */
  decryptText(encryptedData) {
    if (!this.userKey) {
      throw new Error('Encryption not initialized. Call initializeForUser first.');
    }

    if (!encryptedData || typeof encryptedData !== 'string') {
      return encryptedData; // Return as-is if not encrypted
    }

    try {
      const combined = decodeBase64(encryptedData);
      const nonce = combined.slice(0, 24);
      const encrypted = combined.slice(24);

      const decrypted = nacl.secretbox.open(encrypted, nonce, this.userKey);
      if (!decrypted) {
        throw new Error('Decryption failed - invalid data or key');
      }

      return decodeUTF8(decrypted);
    } catch (error) {
      // Return original data if decryption fails (might be unencrypted legacy data)
      return encryptedData;
    }
  }

  /**
   * Encrypt note object before sending to backend
   */
  encryptNote(note) {
    if (!note) return note;

    const encryptedNote = { ...note };
    
    // Encrypt sensitive fields
    if (note.text) {
      encryptedNote.text = this.encryptText(note.text);
    }
    if (note.transcript) {
      encryptedNote.transcript = this.encryptText(note.transcript);
    }
    
    // Mark as encrypted for backend processing
    encryptedNote._encrypted = true;
    
    return encryptedNote;
  }

  /**
   * Decrypt note object after receiving from backend
   */
  decryptNote(encryptedNote) {
    if (!encryptedNote) return encryptedNote;

    const note = { ...encryptedNote };
    
    // Decrypt sensitive fields
    if (encryptedNote.text) {
      note.text = this.decryptText(encryptedNote.text);
    }
    if (encryptedNote.transcript) {
      note.transcript = this.decryptText(encryptedNote.transcript);
    }
    
    // Remove encryption marker
    delete note._encrypted;
    
    return note;
  }

  /**
   * Clear encryption keys from memory
   * Call this on logout for security
   */
  clearKeys() {
    this.userKey = null;
    this.keyCache.clear();
    
    // Clear any cached data that might contain keys
    if (window.crypto && window.crypto.subtle) {
      // Force garbage collection of key material
    }
  }

  /**
   * Check if encryption is available and initialized
   */
  isReady() {
    return !!this.userKey;
  }

  /**
   * Encrypt audio file metadata (filename, etc.)
   * Audio files themselves are stored encrypted by GridFS user isolation
   */
  encryptAudioMetadata(metadata) {
    if (!metadata) return metadata;

    return {
      ...metadata,
      originalName: this.encryptText(metadata.originalName || ''),
      _encrypted: true
    };
  }

  /**
   * Decrypt audio file metadata
   */
  decryptAudioMetadata(encryptedMetadata) {
    if (!encryptedMetadata) return encryptedMetadata;

    const metadata = { ...encryptedMetadata };
    
    if (encryptedMetadata.originalName) {
      metadata.originalName = this.decryptText(encryptedMetadata.originalName);
    }
    
    delete metadata._encrypted;
    return metadata;
  }
}

// Export singleton instance
export const encryptionService = new EncryptionService();
export default encryptionService;